import React, { useState, useEffect, useRef } from 'react';
import { collection, getDocs, addDoc, updateDoc, doc, deleteDoc, query, where, writeBatch } from 'firebase/firestore';
import { db, auth } from '../firebase';
import { ownerStamp } from '../recordOwner';
import HeaderActionsPortal from './HeaderActionsPortal';
import ModuleEditPermissions from './ModuleEditPermissions';
import { useT } from '../i18n.jsx';
import '../theme-palette.css';
import { Calendar as CalendarIcon, Plus, RefreshCw, Edit2, ArrowLeftRight, Lock, Unlock, Trash2, Download, Upload, Check, Shield, CalendarDays } from 'lucide-react';
import { useOutletContext } from 'react-router-dom';
import SchoolAgenda from './SchoolAgenda';

const CalendarAdmin = () => {
  const { t } = useT();
  const slug = (v) => String(v || '').toLowerCase().replace(/[^a-z]/g, '');
  // Day types are stored on the event and compared against their English words all over the
  // platform, so only the shown word is looked up -- with the stored value as the fallback.
  const dayTypeWord = (v) => t(`myCalendar.dayTypes.${slug(v)}`, {}, v);
  const [academicYears, setAcademicYears] = useState([]);
  const [selectedYear, setSelectedYear] = useState(null);
  const [calendarEvents, setCalendarEvents] = useState([]);
  const [timetableProfiles, setTimetableProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDayIds, setSelectedDayIds] = useState([]);
  
  const fileInputRef = useRef(null);

  // Modals
  const [showInitModal, setShowInitModal] = useState(false);
  const [initForm, setInitForm] = useState({
    yearId: '2026-2027', startDate: '2026-09-01', endDate: '2027-08-31'
  });

  const [showAssignModal, setShowAssignModal] = useState(false);
  const [assignForm, setAssignForm] = useState({
    cycleType: 'Continuous', cycleLength: 5, cycleOffset: 2
  });

  const [editDayModal, setEditDayModal] = useState(null); // stores the event object
  const [swapModal, setSwapModal] = useState(false);
  const [swapForm, setSwapForm] = useState({ date1: '', date2: '' });

  const { activeSchoolId, isMobile, user } = useOutletContext();
  const [topTab, setTopTab] = useState('main');

  useEffect(() => {
    if (activeSchoolId) fetchInitialData();
  }, [activeSchoolId]);

  const fetchInitialData = async () => {
    if (!activeSchoolId) return;
    setLoading(true);
    try {
      const pSnap = await getDocs(query(collection(db, 'schools', activeSchoolId, 'timetable_profiles')));
      const pList = [];
      pSnap.forEach(d => pList.push({ uid: d.id, ...d.data() }));
      setTimetableProfiles(pList);

      const ySnap = await getDocs(query(collection(db, 'schools', activeSchoolId, 'academic_years')));
      const yList = [];
      ySnap.forEach(d => yList.push({ uid: d.id, ...d.data() }));
      yList.sort((a, b) => a.yearId.localeCompare(b.yearId));
      setAcademicYears(yList);

      if (yList.length > 0) {
        const currentYear = yList.find(y => y.cur_year === true) || yList[0];
        handleSelectYear(currentYear);
      } else {
        setLoading(false);
      }
    } catch (e) {
      console.error(e);
      setLoading(false);
    }
  };

  const handleSelectYear = async (yearObj) => {
    setSelectedYear(yearObj);
    setSelectedDayIds([]);
    setLoading(true);
    try {
      const eSnap = await getDocs(query(collection(db, 'schools', activeSchoolId, 'calendar_events'), where('yearId', '==', yearObj.yearId)));
      const eList = [];
      eSnap.forEach(d => eList.push({ uid: d.id, ...d.data() }));
      eList.sort((a, b) => a.date.localeCompare(b.date));
      setCalendarEvents(eList);
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  };

  const generateAcademicYear = async (e) => {
    e.preventDefault();
    if (!initForm.yearId || !initForm.startDate || !initForm.endDate) return;

    setLoading(true);
    try {
      // 1. Create Academic Year record
      await addDoc(collection(db, 'schools', activeSchoolId, 'academic_years'), { ...ownerStamp(),
        yearId: initForm.yearId,
        schoolId: activeSchoolId,
        startDate: initForm.startDate,
        endDate: initForm.endDate,
        isLocked: false,
        status: 'Active'
      });

      // 2. Generate Days using Batch
      const batch = writeBatch(db);
      
      let currDate = new Date(initForm.startDate);
      const eDate = new Date(initForm.endDate);

      while (currDate <= eDate) {
        const dateStr = currDate.toISOString().split('T')[0];
        const dayOfWeek = currDate.getDay(); // 0 = Sun, 6 = Sat
        
        const docRef = doc(collection(db, 'schools', activeSchoolId, 'calendar_events'));
        let eventData = {
          date: dateStr,
          yearId: initForm.yearId,
          schoolId: activeSchoolId,
          timetableProfileId: 'default',
          description: ''
        };

        if (dayOfWeek === 0 || dayOfWeek === 6) {
          eventData.type = 'Weekend';
          eventData.cycleDay = '';
        } else {
          eventData.type = 'School Day';
          eventData.cycleDay = '';
        }

        batch.set(docRef, eventData);
        currDate.setDate(currDate.getDate() + 1);
      }

      await batch.commit();
      setShowInitModal(false);
      fetchInitialData();
    } catch (err) {
      alert(t('calendarAdmin.generateFailed', { message: err.message }));
      setLoading(false);
    }
  };

  const handleAssignCycles = async (e) => {
    e.preventDefault();
    if (calendarEvents.length === 0) return;
    setLoading(true);

    try {
      const batch = writeBatch(db);
      const cycleLetters = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H'];
      const weekDays = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
      
      let schoolDaysCount = 0;
      let cycleIndex = 0;

      // Ensure calendarEvents are sorted by date
      const sortedEvents = [...calendarEvents].sort((a, b) => a.date.localeCompare(b.date));

      sortedEvents.forEach(ev => {
        let newCycleDay = '';
        let newDesc = ev.description;

        if (ev.type === 'School Day') {
          schoolDaysCount++;
          
          if (assignForm.cycleType === 'Continuous' && schoolDaysCount <= Number(assignForm.cycleOffset)) {
            newCycleDay = 'Prep Day';
            if (!newDesc) newDesc = 'Preparation / Non-cycle Day';
          } else {
            if (assignForm.cycleType === 'Fixed Week') {
              const dayOfWeek = new Date(ev.date).getDay();
              newCycleDay = weekDays[dayOfWeek];
            } else {
              newCycleDay = `Day ${cycleLetters[cycleIndex % Number(assignForm.cycleLength)]}`;
              cycleIndex++;
            }
          }
        }

        batch.update(doc(db, 'schools', activeSchoolId, 'calendar_events', ev.uid), {
          cycleDay: newCycleDay,
          description: newDesc
        });
      });

      await batch.commit();
      setShowAssignModal(false);
      handleSelectYear(selectedYear); // refresh
    } catch (err) {
      alert(t('calendarAdmin.assignFailed', { message: err.message }));
      setLoading(false);
    }
  };

  const handleToggleLock = async () => {
    if (!selectedYear) return;
    try {
      const newLockedState = !selectedYear.isLocked;
      await updateDoc(doc(db, 'schools', activeSchoolId, 'academic_years', selectedYear.uid), {
        isLocked: newLockedState
      });
      // update local state
      const updatedYear = { ...selectedYear, isLocked: newLockedState };
      setSelectedYear(updatedYear);
      setAcademicYears(academicYears.map(y => y.uid === updatedYear.uid ? updatedYear : y));
    } catch (err) {
      alert(t('calendarAdmin.lockFailed', { message: err.message }));
    }
  };

  const handleSetCurrentYear = async () => {
    if (!selectedYear) return;
    const confirmMsg = t('calendarAdmin.setCurrentYearConfirm', { year: selectedYear.yearId });
    if (!window.confirm(confirmMsg)) return;

    setLoading(true);
    try {
      const batch = writeBatch(db);
      academicYears.forEach(y => {
        if (y.uid !== selectedYear.uid && y.cur_year) {
          batch.update(doc(db, 'schools', activeSchoolId, 'academic_years', y.uid), { cur_year: false });
        }
      });
      batch.update(doc(db, 'schools', activeSchoolId, 'academic_years', selectedYear.uid), { cur_year: true });
      
      await batch.commit();
      fetchInitialData();
    } catch (err) {
      alert(t('calendarAdmin.setCurrentFailed', { message: err.message }));
      setLoading(false);
    }
  };

  const handleDeleteYear = async () => {
    if (!selectedYear) return;
    if (selectedYear.isLocked) return alert(t('calendarAdmin.cannotDeleteLocked'));
    const confirmMsg = t('calendarAdmin.deleteYearConfirm', { year: selectedYear.yearId });
    if (!window.confirm(confirmMsg)) return;

    setLoading(true);
    try {
      // 1. Delete events
      const batch = writeBatch(db);
      calendarEvents.forEach(ev => {
        batch.delete(doc(db, 'schools', activeSchoolId, 'calendar_events', ev.uid));
      });
      await batch.commit();

      // 2. Delete year
      await deleteDoc(doc(db, 'schools', activeSchoolId, 'academic_years', selectedYear.uid));

      setSelectedYear(null);
      setCalendarEvents([]);
      fetchInitialData();
    } catch (err) {
      alert(t('calendarAdmin.deleteYearFailed', { message: err.message }));
      setLoading(false);
    }
  };

  const handleOpenBatchEdit = () => {
    if (selectedDayIds.length === 0) return;
    const selected = calendarEvents.filter(ev => selectedDayIds.includes(ev.uid));
    
    const getCommonValue = (key) => {
      const val = selected[0][key];
      return selected.every(ev => ev[key] === val) ? val : '__MULTI__';
    };

    setEditDayModal({
      isBatch: true,
      type: getCommonValue('type'),
      cycleDay: getCommonValue('cycleDay') || '', // if common is empty string, use it
      timetableProfileId: getCommonValue('timetableProfileId'),
      description: getCommonValue('description') || ''
    });
  };

  const handleUpdateDay = async (e) => {
    e.preventDefault();
    if (!editDayModal) return;
    setLoading(true);
    try {
      const batch = writeBatch(db);
      const idsToUpdate = selectedDayIds;
      
      idsToUpdate.forEach(id => {
        const updates = {};
        if (editDayModal.type !== '__MULTI__') updates.type = editDayModal.type;
        // For cycleDay, if it was 'School Day' type, we update it. If they changed type to Holiday, we should blank cycleDay.
        // But for batch edit, we just apply what they explicitly set.
        if (editDayModal.cycleDay !== '__MULTI__') updates.cycleDay = editDayModal.cycleDay;
        // If they change type to not School Day, force blank cycleDay
        if (editDayModal.type !== '__MULTI__' && editDayModal.type !== 'School Day') updates.cycleDay = '';
        
        if (editDayModal.timetableProfileId !== '__MULTI__') updates.timetableProfileId = editDayModal.timetableProfileId;
        if (editDayModal.description !== '__MULTI__') updates.description = editDayModal.description;
        
        if (Object.keys(updates).length > 0) {
          batch.update(doc(db, 'schools', activeSchoolId, 'calendar_events', id), updates);
        }
      });
      
      await batch.commit();
      setEditDayModal(null);
      setSelectedDayIds([]);
      handleSelectYear(selectedYear); // refresh
    } catch (err) {
      alert(t('calendarAdmin.updateFailed', { message: err.message }));
      setLoading(false);
    }
  };

  const handleExportCSV = () => {
    if (!selectedYear || calendarEvents.length === 0) return;
    
    const headers = ['Date', 'Type', 'Cycle Day', 'Timetable Profile ID', 'Description'];
    const rows = calendarEvents.map(ev => [
      ev.date,
      ev.type || '',
      ev.cycleDay || '',
      ev.timetableProfileId || 'default',
      ev.description || ''
    ]);
    
    const csvContent = [
      headers.join(','),
      ...rows.map(r => r.map(field => `"${field.replace(/"/g, '""')}"`).join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", `Calendar_${selectedYear.yearId}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleImportCSV = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (selectedYear.isLocked) {
      alert(t('calendarAdmin.cannotImportLocked'));
      e.target.value = null;
      return;
    }
    
    const reader = new FileReader();
    reader.onload = async (event) => {
      const text = event.target.result;
      const lines = text.split('\n').map(l => l.trim()).filter(l => l);
      if (lines.length < 2) return alert(t('calendarAdmin.invalidCsv'));

      const parseCSVRow = (str) => {
          const arr = [];
          let quote = false;
          let col = 0;
          for (let c = 0; c < str.length; c++) {
              let cc = str[c], nc = str[c+1];
              arr[col] = arr[col] || '';
              if (cc == '"' && quote && nc == '"') { arr[col] += cc; ++c; continue; }
              if (cc == '"') { quote = !quote; continue; }
              if (cc == ',' && !quote) { ++col; continue; }
              arr[col] += cc;
          }
          return arr;
      };
      
      setLoading(true);
      try {
        const batch = writeBatch(db);
        let updatesMade = 0;
        
        for (let i = 1; i < lines.length; i++) {
          const cols = parseCSVRow(lines[i]);
          if (cols.length < 5) continue;
          
          const date = cols[0];
          const type = cols[1];
          const cycleDay = cols[2];
          const timetableProfileId = cols[3];
          const description = cols[4];
          
          const existingEvent = calendarEvents.find(ev => ev.date === date);
          if (existingEvent) {
            batch.update(doc(db, 'schools', activeSchoolId, 'calendar_events', existingEvent.uid), {
              type,
              cycleDay,
              timetableProfileId,
              description
            });
            updatesMade++;
          }
        }
        
        if (updatesMade > 0) {
          await batch.commit();
          alert(t('calendarAdmin.imported', { count: updatesMade }));
          handleSelectYear(selectedYear); // refresh
        } else {
          alert(t('calendarAdmin.noMatchingDates'));
          setLoading(false);
        }
      } catch (err) {
        alert(t('calendarAdmin.importFailed', { message: err.message }));
        setLoading(false);
      }
    };
    reader.readAsText(file);
    e.target.value = null;
  };

  const formatCycleCode = (cycleStr) => {
    if (!cycleStr) return '';
    const map = { 'Monday': 'M', 'Tuesday': 'T', 'Wednesday': 'W', 'Thursday': 'H', 'Friday': 'F' };
    if (map[cycleStr]) return map[cycleStr];
    if (cycleStr.startsWith('Day ')) return cycleStr.replace('Day ', '');
    if (cycleStr === 'Prep Day') return t('calendarAdmin.prep');
    return cycleStr;
  };

  const handleSwapDays = async (e) => {
    e.preventDefault();
    const e1 = calendarEvents.find(ev => ev.date === swapForm.date1);
    const e2 = calendarEvents.find(ev => ev.date === swapForm.date2);
    
    if (!e1 || !e2) return alert(t('calendarAdmin.swapNotFound'));
    
    try {
      const batch = writeBatch(db);
      
      // Swap cycleDay and type
      batch.update(doc(db, 'schools', activeSchoolId, 'calendar_events', e1.uid), {
        cycleDay: e2.cycleDay,
        type: e2.type,
        timetableProfileId: e2.timetableProfileId
      });
      
      batch.update(doc(db, 'schools', activeSchoolId, 'calendar_events', e2.uid), {
        cycleDay: e1.cycleDay,
        type: e1.type,
        timetableProfileId: e1.timetableProfileId
      });

      await batch.commit();
      setSwapModal(false);
      setSwapForm({ date1: '', date2: '' });
      handleSelectYear(selectedYear); // refresh
    } catch (err) {
      alert(t('calendarAdmin.swapFailed', { message: err.message }));
    }
  };

  // Icon-only on desktop, where the row is tight and a tooltip is one hover away. Labelled in the
  // mobile drawer, because a drawer has room for words and a touch device has no hover -- an
  // icon-only button there is a guessing game with no way to check.
  const iconBtn = {
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.4rem',
    width: 'auto', padding: isMobile ? '0.5rem 0.9rem' : '0.5rem', minWidth: isMobile ? 0 : '38px',
    whiteSpace: 'nowrap',
  };

  return (
    <div className="animate-fade-in new-theme" style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>

      <div className="theme-tabs-bar">
        <div className="theme-tabs-group">
          {[
            { id: 'main', label: t('calendarAdmin.tabs.calendar'), icon: CalendarDays },
            // Everything the school has on, not just the term dates this tab maintains. Put here
            // rather than in a module of its own: the concept already lives on this screen, and the
            // sidebar has just been narrowed rather than widened.
            { id: 'all', label: t('calendarAdmin.tabs.allEvents'), icon: CalendarDays },
            { id: 'settings', label: t('calendarAdmin.tabs.settings'), icon: Shield },
          ].map(tab => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setTopTab(tab.id)}
                className={`theme-tab ${topTab === tab.id ? 'active' : ''}`}
              >
                <div className="theme-tab-icon"><Icon size={20} /></div>
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* One bar per module. These used to sit in a second theme-tabs-bar directly underneath,
            which read as a toolbar wearing the tabs bar's clothes -- every other module carries its
            controls in the tabs row itself. Hidden on the Settings tab because none of them apply
            to it. */}
        {topTab === 'main' && (
          <HeaderActionsPortal>
          <div style={{ display: 'flex', gap: '0.5rem', padding: '0.5rem 1.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
            {academicYears.length === 0 ? (
              <div style={{ color: 'var(--color-text-muted)', fontSize: '0.875rem' }}>{t('calendarAdmin.noYears')}</div>
            ) : (
              <select
                className="input-field"
                value={selectedYear?.uid || ''}
                onChange={(e) => {
                  const yearObj = academicYears.find(y => y.uid === e.target.value);
                  if (yearObj) handleSelectYear(yearObj);
                }}
                aria-label={t('calendarAdmin.academicYear')}
                style={{ width: '180px', fontWeight: '600', padding: '0.5rem', margin: 0 }}
              >
                {academicYears.map(y => (
                  <option key={y.uid} value={y.uid}>
                    {y.yearId}{y.cur_year ? ' (Cur)' : ''}
                  </option>
                ))}
              </select>
            )}
          {selectedYear && (
            <>
              {!selectedYear.cur_year && (
                <button className="btn-outline" onClick={handleSetCurrentYear}
                        title={t('calendarAdmin.setCurrent')} aria-label={t('calendarAdmin.setCurrent')}
                        style={{ ...iconBtn, color: 'var(--color-info)', borderColor: 'var(--color-info)', backgroundColor: 'var(--color-info-bg)' }}>
                  <Check size={16} />{isMobile && ' Set as Current'}
                </button>
              )}
              {selectedDayIds.length > 0 && !selectedYear.isLocked && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', backgroundColor: 'var(--color-bg-app)', padding: '0.25rem 0.5rem', borderRadius: 'var(--radius-md)' }}>
                  <span style={{ fontSize: '0.875rem', fontWeight: '600', color: 'var(--color-text-main)' }}>{selectedDayIds.length} Selected</span>
                  <button className="btn-primary" onClick={handleOpenBatchEdit} style={{ padding: '0.25rem 0.75rem', fontSize: '0.875rem', height: 'auto' }}>
                    <Edit2 size={14} style={{ marginRight: '0.25rem' }} /> Edit
                  </button>
                  <button className="btn-outline" onClick={() => setSelectedDayIds([])} style={{ padding: '0.25rem 0.75rem', fontSize: '0.875rem', height: 'auto' }}>
                    {t('calendarAdmin.clear')}
                  </button>
                </div>
              )}
              {selectedYear.isLocked ? (
                /* A state and a toggle at once, which an icon alone cannot say -- so the tooltip
                   spells out both halves rather than naming just one of them. */
                <button className="btn-outline" onClick={handleToggleLock}
                        title={t('calendarAdmin.locked')} aria-label={t('calendarAdmin.locked')}
                        style={{ ...iconBtn, color: 'var(--color-danger-text)', borderColor: 'var(--color-danger)', backgroundColor: 'var(--color-danger-bg)' }}>
                  <Lock size={16} />{isMobile && ' Locked'}
                </button>
              ) : (
                <>
                  <input type="file" accept=".csv" ref={fileInputRef} style={{ display: 'none' }} onChange={handleImportCSV} />
                  <button className="btn-outline" onClick={() => fileInputRef.current?.click()}
                          title={t('calendarAdmin.importCsv')} aria-label={t('calendarAdmin.importCsv')} style={iconBtn}>
                    <Upload size={16} />{isMobile && ' Import CSV'}
                  </button>
                  <button className="btn-outline" onClick={handleExportCSV}
                          title={t('calendarAdmin.exportCsv')} aria-label={t('calendarAdmin.exportCsv')} style={iconBtn}>
                    <Download size={16} />{isMobile && ' Export CSV'}
                  </button>
                  <div style={{ width: '1px', backgroundColor: 'var(--color-border)', margin: '0 0.35rem', alignSelf: 'stretch' }}></div>
                  <button className="btn-outline" onClick={handleToggleLock}
                          title={t('calendarAdmin.unlocked')} aria-label={t('calendarAdmin.unlocked')}
                          style={{ ...iconBtn, color: 'var(--color-success-text)', borderColor: 'var(--color-success)', backgroundColor: 'var(--color-success-bg)' }}>
                    <Unlock size={16} />{isMobile && ' Unlocked'}
                  </button>
                  <button className="btn-outline" onClick={() => setSwapModal(true)}
                          title={t('calendarAdmin.swapDays')} aria-label={t('calendarAdmin.swapDays')} style={iconBtn}>
                    <ArrowLeftRight size={16} />{isMobile && ' Swap Cycle Days'}
                  </button>
                  <button className="btn-primary" onClick={() => setShowAssignModal(true)}
                          title={t('calendarAdmin.assignCycles')} aria-label={t('calendarAdmin.assignCycles')}
                          style={{ ...iconBtn, backgroundColor: 'var(--color-success)', borderColor: 'var(--color-success)' }}>
                    <RefreshCw size={16} />{isMobile && ' Assign Cycles'}
                  </button>
                  <button className="btn-outline" onClick={handleDeleteYear}
                          title={t('calendarAdmin.deleteYear')} aria-label={t('calendarAdmin.deleteYear')}
                          style={{ ...iconBtn, color: 'var(--color-danger)', borderColor: 'var(--color-danger)' }}>
                    <Trash2 size={16} />{isMobile && ' Delete Year'}
                  </button>
                </>
              )}
            </>
          )}
          <button className="btn-primary" onClick={() => setShowInitModal(true)}
                  title={t('calendarAdmin.addYear')} aria-label={t('calendarAdmin.addYear')}
                  style={{ ...iconBtn, padding: '0.5rem 0.9rem' }}>
            <Plus size={16} /> Year
          </button>
          </div>
          </HeaderActionsPortal>
        )}
      </div>

      {topTab === 'all' && (
        <div style={{ flex: 1, minHeight: 0, padding: isMobile ? '0.75rem' : '0 1.5rem 1.5rem', display: 'flex', flexDirection: 'column' }}>
          <SchoolAgenda schoolId={activeSchoolId} email={user?.email} isMobile={isMobile} />
        </div>
      )}

      {topTab === 'settings' && (
        <div style={{ flex: 1, overflowY: 'auto', padding: isMobile ? 0 : '1.5rem' }}>
          <ModuleEditPermissions
            settingsDoc="calendar_settings"
            moduleId="calendar_admin"
            recordLabel="the school calendar and academic years"
          />
        </div>
      )}

      <div style={{ display: topTab === 'main' ? 'flex' : 'none', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      
      {/* Initialize Modal */}
      {showInitModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'var(--color-overlay)', zIndex: 100, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <div style={{ backgroundColor: 'var(--color-bg-card)', padding: '2rem', borderRadius: 'var(--radius-lg)', width: '100%', maxWidth: '500px' }}>
            <h3 style={{ fontSize: '1.25rem', fontWeight: '700', marginBottom: '1.5rem' }}>{t('calendarAdmin.initTitle')}</h3>
            <form onSubmit={generateAcademicYear} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <div>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.5rem' }}>{t('calendarAdmin.yearName')}</label>
                <input type="text" className="input-field" value={initForm.yearId} onChange={e => setInitForm({...initForm, yearId: e.target.value})} placeholder={t('calendarAdmin.yearNamePlaceholder')} required />
              </div>
              <div style={{ display: 'flex', gap: '1rem' }}>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.5rem' }}>{t('calendarAdmin.startDate')}</label>
                  <input type="date" className="input-field" value={initForm.startDate} onChange={e => setInitForm({...initForm, startDate: e.target.value})} required />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.5rem' }}>{t('calendarAdmin.endDate')}</label>
                  <input type="date" className="input-field" value={initForm.endDate} onChange={e => setInitForm({...initForm, endDate: e.target.value})} required />
                </div>
              </div>
              <div style={{ display: 'flex', gap: '1rem', marginTop: '1rem' }}>
                <button type="button" className="btn-outline" style={{ flex: 1 }} onClick={() => setShowInitModal(false)}>{t('common.cancel')}</button>
                <button type="submit" className="btn-primary" style={{ flex: 1 }}>{t('calendarAdmin.generateYear')}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Assign Cycles Modal */}
      {showAssignModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'var(--color-overlay)', zIndex: 100, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <div style={{ backgroundColor: 'var(--color-bg-card)', padding: '2rem', borderRadius: 'var(--radius-lg)', width: '100%', maxWidth: '500px' }}>
            <h3 style={{ fontSize: '1.25rem', fontWeight: '700', marginBottom: '0.5rem' }}>{t('calendarAdmin.assignTitle')}</h3>
            <p style={{ color: 'var(--color-text-muted)', marginBottom: '1.5rem', fontSize: '0.875rem' }}>
              This will sequentially assign cycle days ONLY to the dates marked as "School Day", perfectly skipping any holidays you have already defined.
            </p>
            <form onSubmit={handleAssignCycles} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <div style={{ display: 'flex', gap: '1rem' }}>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.5rem' }}>{t('calendarAdmin.cycleType')}</label>
                  <select className="input-field" value={assignForm.cycleType} onChange={e => setAssignForm({...assignForm, cycleType: e.target.value})} required>
                    <option value="Continuous">{t('calendarAdmin.continuous')}</option>
                    <option value="Fixed Week">{t('calendarAdmin.fixedWeek')}</option>
                  </select>
                </div>
              </div>
              {assignForm.cycleType === 'Continuous' && (
                <div style={{ display: 'flex', gap: '1rem' }}>
                  <div style={{ flex: 1 }}>
                    <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.5rem' }}>{t('calendarAdmin.cycleLength')}</label>
                    <input type="number" className="input-field" min="1" max="10" value={assignForm.cycleLength} onChange={e => setAssignForm({...assignForm, cycleLength: e.target.value})} required />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.5rem' }}>{t('calendarAdmin.skipPrep')}</label>
                    <input type="number" className="input-field" min="0" value={assignForm.cycleOffset} onChange={e => setAssignForm({...assignForm, cycleOffset: e.target.value})} required />
                    <span style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)' }}>{t('calendarAdmin.skipPrepHint')}</span>
                  </div>
                </div>
              )}
              <div style={{ display: 'flex', gap: '1rem', marginTop: '1rem' }}>
                <button type="button" className="btn-outline" style={{ flex: 1 }} onClick={() => setShowAssignModal(false)}>{t('common.cancel')}</button>
                <button type="submit" className="btn-primary" style={{ flex: 1 }}>{t('calendarAdmin.executeAssignment')}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Day Modal */}
      {editDayModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'var(--color-overlay)', zIndex: 100, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <div style={{ backgroundColor: 'var(--color-bg-card)', padding: '2rem', borderRadius: 'var(--radius-lg)', width: '100%', maxWidth: '400px' }}>
            <h3 style={{ fontSize: '1.25rem', fontWeight: '700', marginBottom: '0.5rem' }}>{selectedDayIds.length > 1
              ? t('calendarAdmin.editDays', { count: selectedDayIds.length })
              : t('calendarAdmin.editDay')}</h3>
            <p style={{ color: 'var(--color-text-muted)', marginBottom: '1.5rem', fontWeight: '600' }}>
              {selectedDayIds.length === 1 ? calendarEvents.find(e => e.uid === selectedDayIds[0])?.date : t('calendarAdmin.multipleDates')}
            </p>
            <form onSubmit={handleUpdateDay} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <div>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.5rem' }}>{t('calendarAdmin.dayType')}</label>
                <select className="input-field" value={editDayModal.type} onChange={e => setEditDayModal({...editDayModal, type: e.target.value})}>
                  {editDayModal.type === '__MULTI__' && <option value="__MULTI__">{t('calendarAdmin.multiValue')}</option>}
                  <option value="School Day">{dayTypeWord('School Day')}</option>
                  <option value="School Holiday">{dayTypeWord('School Holiday')}</option>
                  <option value="Public Holiday">{dayTypeWord('Public Holiday')}</option>
                  <option value="Function Day">{dayTypeWord('Function Day')}</option>
                  <option value="Weekend">{dayTypeWord('Weekend')}</option>
                </select>
              </div>
              {(editDayModal.type === 'School Day' || editDayModal.type === '__MULTI__') && (
                <div>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.5rem' }}>{t('calendarAdmin.cycleOverride')}</label>
                  <input type="text" className="input-field" value={editDayModal.cycleDay} onChange={e => setEditDayModal({...editDayModal, cycleDay: e.target.value})} placeholder={editDayModal.cycleDay === '__MULTI__' ? t('calendarAdmin.multiValue') : 'e.g. Day A'} />
                </div>
              )}
              <div>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.5rem' }}>{t('calendarAdmin.timetableProfile')}</label>
                <select className="input-field" value={editDayModal.timetableProfileId || 'default'} onChange={e => setEditDayModal({...editDayModal, timetableProfileId: e.target.value})}>
                  {editDayModal.timetableProfileId === '__MULTI__' && <option value="__MULTI__">{t('calendarAdmin.multiValue')}</option>}
                  <option value="default">{t('calendarAdmin.defaultProfile')}</option>
                  {timetableProfiles.map(p => <option key={p.uid} value={p.uid}>{p.name}</option>)}
                </select>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.5rem' }}>{t('calendarAdmin.descriptionLabel')}</label>
                <input type="text" className="input-field" value={editDayModal.description} onChange={e => setEditDayModal({...editDayModal, description: e.target.value})} placeholder={editDayModal.description === '__MULTI__' ? 'Multi-value (Unchanged)' : 'e.g. Christmas Holiday'} />
              </div>
              <div style={{ display: 'flex', gap: '1rem', marginTop: '1rem' }}>
                <button type="button" className="btn-outline" style={{ flex: 1 }} onClick={() => setEditDayModal(null)}>{t('common.cancel')}</button>
                <button type="submit" className="btn-primary" style={{ flex: 1 }}>{t('calendarAdmin.saveChanges')}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Swap Modal */}
      {swapModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'var(--color-overlay)', zIndex: 100, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <div style={{ backgroundColor: 'var(--color-bg-card)', padding: '2rem', borderRadius: 'var(--radius-lg)', width: '100%', maxWidth: '400px' }}>
            <h3 style={{ fontSize: '1.25rem', fontWeight: '700', marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}><ArrowLeftRight /> Swap Cycle Days</h3>
            <p style={{ fontSize: '0.875rem', color: 'var(--color-text-muted)', marginBottom: '1.5rem' }}>{t('calendarAdmin.swapIntro')}</p>
            <form onSubmit={handleSwapDays} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <div>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.5rem' }}>{t('calendarAdmin.firstDate')}</label>
                <input type="date" className="input-field" value={swapForm.date1} onChange={e => setSwapForm({...swapForm, date1: e.target.value})} required />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.5rem' }}>{t('calendarAdmin.secondDate')}</label>
                <input type="date" className="input-field" value={swapForm.date2} onChange={e => setSwapForm({...swapForm, date2: e.target.value})} required />
              </div>
              <div style={{ display: 'flex', gap: '1rem', marginTop: '1rem' }}>
                <button type="button" className="btn-outline" style={{ flex: 1 }} onClick={() => setSwapModal(false)}>{t('common.cancel')}</button>
                <button type="submit" className="btn-primary" style={{ flex: 1 }}>{t('calendarAdmin.executeSwap')}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div style={{ display: 'flex', flex: 1, minHeight: 0 }}>
        
        {/* Main: Calendar View */}
        <div style={{ flex: 1, backgroundColor: 'var(--color-bg-card)', borderRadius: isMobile ? 0 : 'var(--radius-lg)', boxShadow: isMobile ? 'none' : 'var(--shadow-sm)', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          
          {loading ? (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
              <RefreshCw className="animate-spin text-muted" size={24} />
            </div>
          ) : !selectedYear ? (
             <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1, color: 'var(--color-text-muted)' }}>
               {t('calendarAdmin.pickYear')}
             </div>
          ) : (
            <>
              <div style={{ padding: '1.5rem', borderBottom: '1px solid var(--color-border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h3 style={{ fontSize: '1.25rem', fontWeight: '700' }}>Calendar Map: {selectedYear.yearId}</h3>
                <div style={{ display: 'flex', gap: '1rem', fontSize: '0.875rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}><div style={{ width: 12, height: 12, backgroundColor: 'var(--color-success-bg)', borderRadius: 2 }}></div> School Day</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}><div style={{ width: 12, height: 12, backgroundColor: 'var(--color-danger-bg)', borderRadius: 2 }}></div> Holiday</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}><div style={{ width: 12, height: 12, backgroundColor: 'var(--color-warning-bg)', borderRadius: 2 }}></div> Function / Prep</div>
                </div>
              </div>
              
              <div style={{ flex: 1, overflowY: 'auto', padding: '1.5rem', display: 'flex', flexDirection: 'column', gap: '3rem' }}>
                {Object.entries(
                  calendarEvents.reduce((acc, ev) => {
                    const month = ev.date.substring(0, 7);
                    if (!acc[month]) acc[month] = [];
                    acc[month].push(ev);
                    return acc;
                  }, {})
                ).map(([monthStr, monthEvents]) => {
                  const dateObj = new Date(monthEvents[0].date);
                  // Find the 1st of this month
                  const firstDayOfMonth = new Date(dateObj.getFullYear(), dateObj.getMonth(), 1);
                  // getDay() is 0=Sun, 1=Mon...
                  const emptyPrefixCount = firstDayOfMonth.getDay();
                  
                  // Actually, just loop from 1 to the date of the first event to create empty cells, plus the month's start padding.
                  const firstEventDateNum = parseInt(monthEvents[0].date.split('-')[2], 10);
                  const totalEmptyCells = emptyPrefixCount + (firstEventDateNum - 1);

                  const monthName = firstDayOfMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

                  return (
                    <div key={monthStr}>
                      <h4 style={{ fontSize: '1.25rem', fontWeight: '700', marginBottom: '1rem', color: 'var(--color-primary)' }}>{monthName}</h4>
                      
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '0.5rem', marginBottom: '0.5rem' }}>
                        {['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'].map(d => (
                          <div key={d} style={{ textAlign: 'center', fontWeight: '700', fontSize: '0.875rem', color: 'var(--color-text-muted)' }}>{t(`calendarAdmin.weekdays.${d}`)}</div>
                        ))}
                      </div>

                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '0.5rem' }}>
                        {/* Empty padding cells */}
                        {Array.from({ length: totalEmptyCells }).map((_, i) => (
                          <div key={`empty-${i}`} style={{ backgroundColor: 'transparent', minHeight: '80px' }} />
                        ))}
                        
                        {/* Actual days */}
                        {monthEvents.map(ev => {
                          let bg = 'var(--color-bg-subtle)';
                          if (ev.type === 'School Day' && ev.cycleDay && !ev.cycleDay.includes('Prep')) bg = 'var(--color-success-bg)';
                          else if (ev.type.includes('Holiday')) bg = 'var(--color-danger-bg)';
                          else if (ev.type === 'Function Day' || ev.cycleDay === 'Prep Day') bg = 'var(--color-warning-bg)';

                          const profile = timetableProfiles.find(p => p.uid === ev.timetableProfileId);
                          const isDefault = !ev.timetableProfileId || ev.timetableProfileId === 'default';
                          const dateNum = parseInt(ev.date.split('-')[2], 10);
                          const isSelected = selectedDayIds.includes(ev.uid);

                          return (
                            <div 
                              key={ev.uid} 
                              onClick={() => {
                                if (!selectedYear?.isLocked) {
                                  if (selectedDayIds.includes(ev.uid)) {
                                    setSelectedDayIds(selectedDayIds.filter(id => id !== ev.uid));
                                  } else {
                                    setSelectedDayIds([...selectedDayIds, ev.uid]);
                                  }
                                }
                              }}
                              style={{ 
                                backgroundColor: bg, padding: '0.75rem', borderRadius: 'var(--radius-md)', 
                                border: isSelected ? '2px solid var(--color-primary)' : '1px solid var(--color-border)', 
                                cursor: selectedYear?.isLocked ? 'default' : 'pointer',
                                display: 'flex', flexDirection: 'column', gap: '0.25rem', minHeight: '90px',
                                transition: 'transform 0.1s, box-shadow 0.1s',
                                opacity: selectedYear?.isLocked ? 0.8 : 1,
                                boxShadow: isSelected ? '0 0 0 2px var(--color-primary-light)' : 'none'
                              }}
                              onMouseEnter={(e) => {
                                if (!selectedYear?.isLocked) e.currentTarget.style.transform = 'scale(1.05)';
                              }}
                              onMouseLeave={(e) => {
                                if (!selectedYear?.isLocked) e.currentTarget.style.transform = 'scale(1)';
                              }}
                            >
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                <div style={{ fontWeight: '800', fontSize: '1.1rem', color: 'var(--color-primary)' }}>
                                  {formatCycleCode(ev.cycleDay)}
                                </div>
                                <div style={{ fontWeight: '700', fontSize: '0.875rem', color: 'var(--color-text-main)' }}>{dateNum}</div>
                              </div>
                              
                              <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
                                {ev.description ? (
                                  <span style={{ fontSize: '0.85rem', fontWeight: '700', color: 'var(--color-text-main)', textAlign: 'center', wordBreak: 'break-word' }}>
                                    {ev.description}
                                  </span>
                                ) : (!ev.cycleDay || ev.cycleDay === 'Prep Day') && (
                                  <span style={{ fontSize: '0.85rem', fontWeight: '700', color: 'var(--color-text-muted)', textAlign: 'center' }}>
                                    {ev.type}
                                  </span>
                                )}
                              </div>
                              
                              {!isDefault && ev.type === 'School Day' && (
                                <div style={{ fontSize: '0.6rem', color: 'var(--color-warning-text)', backgroundColor: 'var(--color-warning-bg)', padding: '0.1rem 0.2rem', borderRadius: '4px', textAlign: 'center', marginTop: 'auto' }}>
                                  {profile?.shortCode || profile?.name || t('calendarAdmin.customProfile')}
                                </div>
                              )}
                            </div>
                          )
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>

      </div>
      </div>
    </div>
  );
};

export default CalendarAdmin;
